import React, {createContext, useContext, useState, useEffect, useMemo} from 'react';
import {observer} from 'mobx-react';
import {useCompanyConfiguration, useMyCompanyConfiguration} from 'src/autobahn/features/company/queries';
import {CompanyConfiguration} from 'src/core/data/api/CompanyConfiguration';
import {DEFAULT_LOGO_URL, DEFAULT_THEME} from 'src/autobahn/shared/constants';
import useStore from 'src/hooks/use-store';

export const CompanyContext = createContext<CompanyConfiguration & { hasLogoLoaded: boolean } | null>(null);

const getHostname = (): string => {
    return process.env.REACT_APP_COMPANY_HOSTNAME || window.location.hostname;
};

const buildDefaultCompany = (hostname: string): CompanyConfiguration => ({
    id: 0,
    customDomain: hostname,
    logoUrl: DEFAULT_LOGO_URL,
    theme: DEFAULT_THEME,
    androidAppUrl: '',
    iosAppUrl: '',
    monitoringCenterContact: undefined,
    customerAreaContact: undefined,
    customerAreaLink: undefined,
    assistanceService: false,
    monitoringCenterService: false,
});

const normalizeCompany = (company: CompanyConfiguration | null | undefined, hostname: string): CompanyConfiguration => {
    if (!company) {
        return buildDefaultCompany(hostname);
    }

    return {
        ...company,
        logoUrl: company.logoUrl || DEFAULT_LOGO_URL,
        theme: company.theme || DEFAULT_THEME,
    };
};

const CompanyProvider = ({children}: { children: React.ReactNode }) => {
    const {store} = useStore();
    const {auth} = store;
    const hostname = getHostname();

    const {data: rawCompanyByHostname, isLoading} = useCompanyConfiguration(hostname);
    const {
        data: rawMyCompany, isSuccess: hasMyCompany, isInitialLoading, failureCount,
    } = useMyCompanyConfiguration(auth.isLogged);
    // Só a primeira tentativa segura a tela: se ela falhar (rede, 5xx), o app segue com a marca do domínio em vez
    // de ficar em branco durante as tentativas seguintes, e troca para a da central quando uma delas responder.
    const isLoadingMyCompany = isInitialLoading && failureCount === 0;

    // Dominio/subdominio personaliza antes do login (tela de login nao sabe quem vai entrar).
    // Depois de autenticado, a marca passa a ser a da central vinculada -- so cai de volta pro
    // dominio/subdominio se essa central nao tiver configuracao propria (hasMyCompany false).
    const rawCompany = (auth.isLogged && hasMyCompany) ? rawMyCompany : rawCompanyByHostname;

    const currentCompany = useMemo(
        () => normalizeCompany(rawCompany, hostname),
        [rawCompany, hostname],
    );

    const [hasLogoLoaded, setLogoLoaded] = useState<boolean>(false);

    useEffect(() => {
        if (isLoadingMyCompany) return;
        if (currentCompany?.theme) {
            store.updateTheme(currentCompany.theme);
        }
    }, [currentCompany?.theme, isLoadingMyCompany]);

    useEffect(() => {
        if (isLoadingMyCompany || !currentCompany?.logoUrl) return;

        setLogoLoaded(false);
        const image = new Image();
        image.onload = () => setLogoLoaded(true);
        image.onerror = () => setLogoLoaded(true);
        image.src = currentCompany.logoUrl;

        return () => {
            image.onload = null;
            image.onerror = null;
        };
    }, [currentCompany?.logoUrl, isLoadingMyCompany]);

    // Só a consulta do domínio, que é a primeira da sessão, segura a tela. Depois do login a árvore continua montada
    // (a tela de login precisa terminar o que começou), então, enquanto a central do usuário não responde, o
    // contexto não entrega o logo do domínio nem diz que o logo carregou: o tema e o logo só trocam quando ela
    // responde, e quem consome mostra o esqueleto no lugar de uma marca que vai mudar.
    if (isLoading) return null;

    const value = isLoadingMyCompany ?
        {...currentCompany, logoUrl: '', hasLogoLoaded: false} :
        {...currentCompany, hasLogoLoaded};

    return (
        <CompanyContext.Provider value={value}>
            {children}
        </CompanyContext.Provider>
    );
};

export function useCompany() {
    return useContext(CompanyContext);
}

export default observer(CompanyProvider);
